/* ── CV review and releases (CvReviewScreen.web.tsx) ─────────────────────
   Web only. Imported after global.css by src/app/_layout.web.tsx. One page
   column (820), the CV read as a document on a paper sheet, approval at the
   sheet's foot, and three tabs: Review, Languages, History. Neutral tokens
   only; #d93025 is reserved for errors and the unpublish action. */

.tabbio-cvr-page {
  --cvr-tint: rgba(0, 0, 0, 0.035);
  /* Fill the route's box whether the parent is a block, a flex row or the
     CV layout's flex column; the column inside centres itself. */
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  background: var(--bg);
  color: var(--text-primary);
  /* The body carries the locale's stack (Google Sans, or Cairo for Arabic). */
  font-family: inherit;
}
[data-theme="dark"] .tabbio-cvr-page {
  --cvr-tint: rgba(255, 255, 255, 0.04);
  /* Native checkboxes, inputs and scrollbars follow the dark surface. */
  color-scheme: dark;
}
.tabbio-cvr-body {
  max-width: 820px;
  margin-inline: auto;
  padding: 32px 24px 96px;
}
.tabbio-cvr-muted {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 21px;
}
.tabbio-cvr-empty {
  margin: 0;
  padding: 32px 16px;
  color: var(--text-secondary);
  font-size: 14px;
  text-align: center;
}
.tabbio-cvr-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.tabbio-cvr-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/* Buttons: outline pill by default; one solid neutral primary per area. */
.tabbio-cvr-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid var(--input-border);
  border-radius: 9999px;
  background: var(--input-bg);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  text-align: start;
  cursor: pointer;
  transition: background-color 150ms, opacity 150ms;
}
.tabbio-cvr-btn .material-symbols-outlined {
  font-size: 18px;
  color: var(--text-secondary);
}
.tabbio-cvr-btn:hover:not(:disabled) {
  background: var(--hover);
}
.tabbio-cvr-btn.is-primary {
  border-color: transparent;
  background: var(--text-primary);
  color: var(--bg);
}
.tabbio-cvr-btn.is-primary .material-symbols-outlined {
  color: inherit;
}
.tabbio-cvr-btn.is-primary:hover:not(:disabled) {
  background: var(--text-primary);
  opacity: 0.88;
}
.tabbio-cvr-btn.is-ghost {
  padding-inline: 12px;
  border-color: transparent;
  background: transparent;
}
.tabbio-cvr-btn.is-danger {
  border-color: transparent;
  background: transparent;
  color: #d93025;
}
.tabbio-cvr-btn.is-danger .material-symbols-outlined {
  color: inherit;
}
.tabbio-cvr-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.tabbio-cvr-page button:focus-visible,
.tabbio-cvr-input:focus-visible {
  outline: 2px solid var(--text-primary);
  outline-offset: 2px;
}

/* Header row under the page title: actions at the start, state at the end. */
.tabbio-cvr-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
}
.tabbio-cvr-status {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tabbio-cvr-status .tabbio-menu-badge {
  font-size: 12px;
}

/* Original / English / Arabic */
.tabbio-cvr-editions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 20px;
}
.tabbio-cvr-editions-label {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}
.tabbio-cvr-editions-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tabbio-cvr-edition {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 34px;
  padding: 0 14px;
  border: 1px solid var(--input-border);
  border-radius: 9999px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.tabbio-cvr-edition:hover:not(:disabled) {
  background: var(--hover);
}
.tabbio-cvr-edition[aria-checked="true"] {
  border-color: transparent;
  background: var(--hover-strong);
}
.tabbio-cvr-edition:disabled {
  opacity: 0.45;
  cursor: default;
}

/* Tabs: text with an underline, the row sits on a hairline. */
.tabbio-cvr-tabs {
  display: flex;
  gap: 4px;
  margin: 24px 0 24px;
  border-bottom: 1px solid var(--input-border);
  overflow-x: auto;
  scrollbar-width: none;
}
.tabbio-cvr-tab {
  position: relative;
  flex-shrink: 0;
  min-height: 44px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px 10px 0 0;
  background: transparent;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.tabbio-cvr-tab:hover:not(:disabled) {
  color: var(--text-primary);
  background: var(--hover);
}
.tabbio-cvr-tab:disabled {
  opacity: 0.45;
  cursor: default;
}
.tabbio-cvr-tab[aria-selected="true"] {
  color: var(--text-primary);
}
.tabbio-cvr-tab[aria-selected="true"]::after {
  content: "";
  position: absolute;
  inset-inline: 12px;
  bottom: -1px;
  height: 2px;
  border-radius: 2px;
  background: var(--text-primary);
}
.tabbio-cvr-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Notices: a tinted band with a leading glyph and optional actions. */
.tabbio-cvr-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: var(--cvr-tint);
  color: var(--text-primary);
  font-size: 14px;
  line-height: 21px;
}
.tabbio-cvr-notice-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--text-secondary);
}
.tabbio-cvr-notice-body {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}
.tabbio-cvr-notice-body > .tabbio-cvr-actions {
  margin-inline-start: -12px;
}
.tabbio-cvr-notice.is-error,
.tabbio-cvr-notice.is-error .tabbio-cvr-notice-icon {
  color: #d93025;
  font-weight: 500;
}

/* The paper sheet: the CV reads on its own surface, actions at its foot. */
.tabbio-cvr-sheet {
  overflow: hidden;
  border: 1px solid var(--input-border);
  border-radius: 20px;
  background: var(--input-bg);
  box-shadow: var(--gem-input-shadow);
}
.tabbio-cvr-sheet-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
  padding: 18px 24px 0;
}
.tabbio-cvr-sheet-title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
}
.tabbio-cvr-sheet-meta {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: var(--text-tertiary);
  font-size: 13px;
}
.tabbio-cvr-sheet-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 24px 24px;
}
.tabbio-cvr-sheet-foot {
  padding: 16px 24px 20px;
  border-top: 1px solid var(--input-border);
  background: var(--cvr-tint);
}
.tabbio-cvr-approve {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
}
.tabbio-cvr-approve > .tabbio-cvr-muted {
  flex: 1 1 280px;
  font-size: 13px;
  line-height: 19px;
}
.tabbio-cvr-inset {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 16px;
  background: var(--cvr-tint);
}
.tabbio-cvr-caption {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
}
.tabbio-cvr-health {
  padding-top: 4px;
}
.tabbio-cvr-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 21px;
  cursor: pointer;
}
.tabbio-cvr-check input {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: var(--text-primary);
  cursor: inherit;
}
.tabbio-cvr-check:has(input:disabled) {
  opacity: 0.45;
  cursor: default;
}

/* While editing: Save and Discard pinned to the top of the page scroller. */
.tabbio-cvr-editbar {
  position: sticky;
  top: 8px;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  padding: 12px 14px;
  border: 1px solid var(--input-border);
  border-radius: 16px;
  background: var(--menu-bg);
  box-shadow: var(--gem-menu-shadow);
}
.tabbio-cvr-editbar-icon {
  flex-shrink: 0;
  color: var(--text-secondary);
}
.tabbio-cvr-editbar-text {
  display: flex;
  flex: 1 1 220px;
  min-width: 0;
  flex-direction: column;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 18px;
}
.tabbio-cvr-editbar-text strong {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

/* Skeleton lines while a document loads (static: no shimmer). */
.tabbio-cvr-loading {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}
.tabbio-cvr-skeleton {
  display: block;
  height: 12px;
  border-radius: 6px;
  background: var(--hover-strong);
}

/* ── The CV document ─────────────────────────────────────────────────── */
.tabbio-cvr-doc {
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 22px;
  text-align: start;
}
.tabbio-cvr-doc-name {
  margin: 0;
  font-size: 22px;
  font-weight: 500;
  line-height: 28px;
}
.tabbio-cvr-doc-headline {
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 22px;
}
.tabbio-cvr-doc-contact {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 10px;
  color: var(--text-tertiary);
  font-size: 13px;
}
.tabbio-cvr-doc-contact-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow-wrap: anywhere;
}
.tabbio-cvr-doc-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.tabbio-cvr-doc-section-title {
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--input-border);
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
}
.tabbio-cvr-doc-entry {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tabbio-cvr-doc-entry + .tabbio-cvr-doc-entry {
  padding-top: 6px;
}
.tabbio-cvr-doc-entry-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 2px 16px;
}
.tabbio-cvr-doc-entry-title {
  margin: 0;
  min-width: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
}
.tabbio-cvr-doc-period {
  color: var(--text-tertiary);
  font-size: 13px;
  white-space: nowrap;
}
.tabbio-cvr-doc-entry-meta {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 19px;
}
.tabbio-cvr-doc-text {
  margin: 4px 0 0;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.tabbio-cvr-doc-section > .tabbio-cvr-doc-text {
  margin-top: 0;
}
.tabbio-cvr-doc-bullets {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 6px 0 0;
  padding-inline-start: 20px;
}
.tabbio-cvr-doc-bullets li::marker {
  color: var(--text-tertiary);
}
.tabbio-cvr-doc-link {
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 13px;
  overflow-wrap: anywhere;
}
.tabbio-cvr-doc-skills {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.tabbio-cvr-doc-skill-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tabbio-cvr-doc-skill-category {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}
.tabbio-cvr-doc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tabbio-cvr-doc-chip {
  padding: 3px 10px;
  border-radius: 9999px;
  background: var(--hover);
  font-size: 13px;
  line-height: 20px;
}

/* Edit view: every field as an input, grouped by entry. */
.tabbio-cvr-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.tabbio-cvr-fieldset {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
.tabbio-cvr-fieldset + .tabbio-cvr-fieldset {
  padding-top: 16px;
  border-top: 1px solid var(--input-border);
}
.tabbio-cvr-legend {
  margin-bottom: 4px;
  padding: 0;
  font-size: 15px;
  font-weight: 500;
}
.tabbio-cvr-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tabbio-cvr-field-label {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}
.tabbio-cvr-input {
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid var(--input-border);
  border-radius: 14px;
  background: var(--input-bg);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 15px;
  line-height: 22px;
  resize: vertical;
}
.tabbio-cvr-input:focus {
  border-color: var(--text-primary);
}
.tabbio-cvr-input:disabled {
  opacity: 0.6;
}

/* ── Languages and History ───────────────────────────────────────────── */
.tabbio-cvr-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.tabbio-cvr-group-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}
.tabbio-cvr-history-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
}
.tabbio-cvr-history-head > .tabbio-cvr-muted {
  flex: 1 1 320px;
}
.tabbio-cvr-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.tabbio-cvr-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 60px;
  padding: 10px 12px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  text-align: start;
  cursor: pointer;
}
.tabbio-cvr-row:hover:not(:disabled) {
  background: var(--hover);
}
.tabbio-cvr-row[aria-pressed="true"] {
  background: var(--hover-strong);
}
.tabbio-cvr-row:disabled {
  opacity: 0.45;
  cursor: default;
}
.tabbio-cvr-row-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--hover);
  color: var(--text-secondary);
}
.tabbio-cvr-row-body {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
}
.tabbio-cvr-row-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.tabbio-cvr-row-sub {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 18px;
}
.tabbio-cvr-row-trailing {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.tabbio-cvr-row-date {
  color: var(--text-tertiary);
  font-size: 12px;
  white-space: nowrap;
}
.tabbio-cvr-row-chevron {
  color: var(--text-tertiary);
}
.tabbio-cvr-compare {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.tabbio-cvr-compare-col {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 10px;
}
.tabbio-cvr-paper {
  padding: 18px 20px;
  border: 1px solid var(--input-border);
  border-radius: 16px;
  background: var(--input-bg);
}
.tabbio-cvr-paper .tabbio-cvr-doc {
  gap: 20px;
}
.tabbio-cvr-paper .tabbio-cvr-doc-name {
  font-size: 18px;
  line-height: 24px;
}
.tabbio-cvr-reasons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding-inline-start: 18px;
}
.tabbio-cvr-changes {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
/* Which saved version each side of the comparison is. */
.tabbio-cvr-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 18px;
}
.tabbio-cvr-change {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: var(--cvr-tint);
}
.tabbio-cvr-change-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.tabbio-cvr-change-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 16px;
  font-size: 14px;
  line-height: 21px;
}
.tabbio-cvr-change-row + .tabbio-cvr-change-row {
  padding-top: 12px;
  border-top: 1px solid var(--input-border);
}
.tabbio-cvr-change-field {
  grid-column: 1 / -1;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
}
/* Neither side is "after": the reader picks which two versions to compare. */
.tabbio-cvr-change-side {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  overflow-wrap: anywhere;
}
.tabbio-cvr-change-side .tabbio-cvr-caption {
  color: var(--text-tertiary);
  font-size: 12px;
  font-weight: 400;
}
.tabbio-cvr-restore {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 16px;
  background: var(--cvr-tint);
}

@media (max-width: 720px) {
  .tabbio-cvr-compare,
  .tabbio-cvr-change-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 640px) {
  .tabbio-cvr-body {
    padding: 20px 16px 48px;
  }
  .tabbio-cvr-sheet-head {
    padding: 16px 18px 0;
  }
  .tabbio-cvr-sheet-body {
    padding: 16px 18px 20px;
  }
  .tabbio-cvr-sheet-foot {
    padding: 14px 18px 18px;
  }
  .tabbio-cvr-approve .tabbio-cvr-btn {
    width: 100%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .tabbio-cvr-btn {
    transition: none;
  }
}
